import { useEffect, useMemo, type FC } from "react";
import { useSelector } from "@legendapp/state/react";
import { useUnmountEffect } from "primereact/hooks";
import { Skeleton } from "primereact/skeleton";
import ArrivalPoiTable from "@/pages/CampaignOverviewPage/components/ArrivalPoiTable";
import { isAgencyUser } from "@/stores/userStore";
import ChannelBreakdownWidget from "@/pages/CampaignOverviewPage/widgets/ChannelBreakdownWidget";
import CreativePerformanceWidget from "@/pages/CampaignOverviewPage/widgets/CreativePerformanceWidget";
import GeoWidget from "@/pages/CampaignOverviewPage/widgets/GeoWidget";
import VideoFunnelWidget from "@/pages/CampaignOverviewPage/widgets/VideoFunnelWidget";
import CampaignEmptyState from "@/pages/CampaignOverviewPage/page/CampaignEmptyState";
import CampaignOverviewHeader from "@/pages/CampaignOverviewPage/page/CampaignOverviewHeader";
import { deriveCampaignOverviewHeaderProps } from "@/pages/CampaignOverviewPage/utils/deriveCampaignOverviewHeaderProps";
import KpiTiles from "@/pages/CampaignOverviewPage/widgets/KpiTiles";
import PerformanceChart from "@/pages/CampaignOverviewPage/widgets/PerformanceChart";
import TopPerformers from "@/pages/CampaignOverviewPage/widgets/TopPerformers";
import useHasVideoLayout from "@/hooks/useHasVideoLayout";
import { hasVideoData } from "@/pages/CampaignOverviewPage/utils/videoFunnelStages";
import PerformanceTable from "@/pages/CampaignOverviewPage/widgets/PerformanceTable";
import { resolveDateRange } from "@/pages/CampaignOverviewPage/utils/resolveDateRange";
import styles from "@/pages/CampaignOverviewPage/page/CampaignOverviewPage.module.css";
import { AdvertisersStore$ } from "@/stores/advertisersStore";
import { AgenciesStore$ } from "@/stores/agenciesStore";
import { ArrivalStore$, fetchArrivalReport } from "@/stores/arrivalStore";
import {
  ArrivalPoiStore$,
  fetchArrivalPoiReport,
} from "@/stores/arrivalPoiStore";
import { CampaignsStore$ } from "@/stores/campaignsStore";
import { FilterStore$ } from "@/stores/filterStore";
import {
  ChannelBreakdownStore$,
  fetchChannelBreakdownReport,
} from "@/stores/channelBreakdownStore";
import {
  fetchPerformanceReport,
  ReportingStore$,
} from "@/stores/reportingStore";
import {
  AudienceSegmentStore$,
  fetchAudienceSegment,
} from "@/stores/audienceSegmentStore";
import {
  CreativePerformanceStore$,
  fetchCreativePerformance,
} from "@/stores/creativePerformanceStore";
import {
  DeviceBrowserStore$,
  fetchDeviceBrowserReport,
} from "@/stores/deviceBrowserStore";
import { GeoStore$, fetchGeoReport } from "@/stores/geoStore";
import {
  fetchPerformanceTable,
  PerformanceTableStore$,
} from "@/stores/performanceTableStore";
import {
  fetchVideoFunnelReport,
  VideoFunnelStore$,
} from "@/stores/videoFunnelStore";
import {
  fetchViewabilityReport,
  ViewabilityStore$,
} from "@/stores/viewabilityStore";
import ArrivalWidget from "@/pages/CampaignOverviewPage/widgets/ArrivalWidget";
import ViewabilityWidget from "@/pages/CampaignOverviewPage/widgets/ViewabilityWidget";
import { getDefaultOrganizationId } from "@/utils/organization";
import { buildReportingScopeFilters } from "@/utils/reportingScope";

// The header label must only ever describe data this page is showing, so it is
// cleared whenever the range can't be resolved and when the page unmounts.
const clearResolvedDateRange = (): void => {
  FilterStore$.resolvedDateRange.set({ end: null, start: null });
};

const CampaignOverviewPage: FC = () => {
  const agencyUser = useSelector(() => isAgencyUser());
  const agency = useSelector(() => FilterStore$.agency.get());
  const advertiser = useSelector(() => FilterStore$.advertiser.get());
  const selectedCampaignIds = useSelector(() => FilterStore$.campaign.get());
  const campaigns = useSelector(() => CampaignsStore$.campaigns.get());
  const campaignsLoading = useSelector(() => CampaignsStore$.isLoading.get());
  const campaignsError = useSelector(() => CampaignsStore$.error.get());
  const campaignsLoadedKey = useSelector(() =>
    CampaignsStore$.loadedForScopeKey.get()
  );
  const advertiserInitialized = useSelector(() =>
    AdvertisersStore$.advertiserInitialized.get()
  );
  const agencyInitialized = useSelector(() =>
    AgenciesStore$.agencyInitialized.get()
  );
  const advertisers = useSelector(() => AdvertisersStore$.advertisers.get());
  const agencies = useSelector(() => AgenciesStore$.agencies.get());
  const datePreset = useSelector(() => FilterStore$.datePreset.get());
  const customDateRange = useSelector(() => FilterStore$.dateRange.get());
  const reportingRows = useSelector(() => ReportingStore$.rows.get());
  const reportingLoading = useSelector(() => ReportingStore$.isLoading.get());
  const reportingError = useSelector(() => ReportingStore$.error.get());
  const arrivalRows = useSelector(() => ArrivalStore$.rows.get());
  const arrivalLoading = useSelector(() => ArrivalStore$.isLoading.get());
  const arrivalError = useSelector(() => ArrivalStore$.error.get());
  const arrivalPoiRows = useSelector(() => ArrivalPoiStore$.rows.get());
  const arrivalPoiLoading = useSelector(() => ArrivalPoiStore$.isLoading.get());
  const arrivalPoiError = useSelector(() => ArrivalPoiStore$.error.get());
  const channelRows = useSelector(() => ChannelBreakdownStore$.rows.get());
  const channelLoading = useSelector(() =>
    ChannelBreakdownStore$.isLoading.get()
  );
  const channelError = useSelector(() => ChannelBreakdownStore$.error.get());
  const deviceBrowserRows = useSelector(() => DeviceBrowserStore$.rows.get());
  const deviceBrowserLoading = useSelector(() =>
    DeviceBrowserStore$.isLoading.get()
  );
  const deviceBrowserError = useSelector(() => DeviceBrowserStore$.error.get());
  const audienceSegmentRows = useSelector(() =>
    AudienceSegmentStore$.rows.get()
  );
  const audienceSegmentLoading = useSelector(() =>
    AudienceSegmentStore$.isLoading.get()
  );
  const audienceSegmentError = useSelector(() =>
    AudienceSegmentStore$.error.get()
  );
  const creativeRows = useSelector(() => CreativePerformanceStore$.rows.get());
  const creativeLoading = useSelector(() =>
    CreativePerformanceStore$.isLoading.get()
  );
  const creativeError = useSelector(() =>
    CreativePerformanceStore$.error.get()
  );
  const geoRows = useSelector(() => GeoStore$.rows.get());
  const geoLoading = useSelector(() => GeoStore$.isLoading.get());
  const geoError = useSelector(() => GeoStore$.error.get());
  const tableRows = useSelector(() => PerformanceTableStore$.rows.get());
  const tableLoading = useSelector(() =>
    PerformanceTableStore$.isLoading.get()
  );
  const tableError = useSelector(() => PerformanceTableStore$.error.get());
  const videoFunnelRows = useSelector(() => VideoFunnelStore$.rows.get());
  const videoFunnelLoading = useSelector(() =>
    VideoFunnelStore$.isLoading.get()
  );
  const videoFunnelError = useSelector(() => VideoFunnelStore$.error.get());
  const videoFunnelLoadedKey = useSelector(() =>
    VideoFunnelStore$.loadedForScopeKey.get()
  );
  const viewabilityRows = useSelector(() => ViewabilityStore$.rows.get());
  const viewabilityLoading = useSelector(() =>
    ViewabilityStore$.isLoading.get()
  );
  const viewabilityError = useSelector(() => ViewabilityStore$.error.get());

  const selectedCampaigns = useMemo(() => {
    const selectedSet = new Set(selectedCampaignIds);
    return campaigns.filter((item) => selectedSet.has(item.id));
  }, [campaigns, selectedCampaignIds]);

  // Empty only when the user has intentionally cleared their top-level selector.
  // Agency users control from Advertisers (their agency is always auto-set);
  // Infillion users control from Agencies. Both are gated on their
  // "initialized" flag so the pre-default window (initial load, before the
  // breadcrumb has applied its defaults) renders skeletons, not the empty state.
  const rootEmpty = agencyUser
    ? advertiserInitialized && advertiser.length === 0
    : agencyInitialized && agency.length === 0;

  // For Infillion users: advertiser is the mid-level selector. Once initialized
  // (first default has fired), clearing it is intentional — treat like rootEmpty.
  // advertiserInitialized gates this so the pre-default cascade window (where
  // advertisers have loaded but the default effect hasn't fired yet) doesn't
  // incorrectly trigger the empty state.
  const advertiserEmpty =
    !agencyUser && advertiserInitialized && advertiser.length === 0;

  // True only while the cascade is still settling — three transient windows:
  //   1. campaigns are actively loading
  //   2. IDs are queued but campaign objects haven't resolved (brief useMemo gap)
  //   3. filter just reset: no IDs yet, campaign list empty, and fetch not yet
  //      committed (campaignsLoadedKey === null). Once the fetch completes —
  //      even with zero results — loadedForScopeKey is set, disambiguating a
  //      genuine "agency has no campaigns" result from the pre-fetch gap.
  // NOT true when campaigns are available but user chose none, or when the
  // advertiser selector was intentionally cleared — both fall to the guard below.
  const isTransitioning =
    !rootEmpty &&
    !advertiserEmpty &&
    (campaignsLoading ||
      (selectedCampaignIds.length > 0 && selectedCampaigns.length === 0) ||
      (selectedCampaignIds.length === 0 &&
        campaigns.length === 0 &&
        campaignsLoadedKey === null &&
        !campaignsError));

  useEffect(() => {
    // Cleared first so every early return below leaves no stale label behind.
    clearResolvedDateRange();
    // Derive inside the effect from stable deps — selectedCampaigns is a fresh
    // array reference on every render (.filter()), so depending on it would
    // re-fire this effect on every ReportingStore$/ArrivalStore$ update while
    // a fetch is in flight, firing duplicate requests before loadedForScopeKey
    // is set.
    const filtered = campaigns.filter((item) =>
      selectedCampaignIds.includes(item.id)
    );
    if (filtered.length === 0) return;
    // If selectedCampaignIds includes IDs that campaigns no longer contains,
    // we're in the brief window between the campaign list updating and
    // HierarchyBreadcrumb's [campaigns] effect reconciling the selection.
    // Skip now; the effect re-fires once selectedCampaignIds settles.
    if (filtered.length < selectedCampaignIds.length) return;
    // An advertiser change reaches this effect before the campaign list for
    // it does; waiting for that list keeps the brief mixed state (new
    // advertisers, old campaigns) from sending a round of requests.
    if (campaignsLoading) return;
    const scopeFilters = buildReportingScopeFilters({
      advertiserIds: advertisers.map((item) => item.id),
      agencyIds: agencies.map((item) => item.id),
      agencyUser,
      campaignIds: campaigns.map((item) => item.id),
      organizationId: getDefaultOrganizationId(),
      selectedAdvertiserIds: advertiser,
      selectedAgencyIds: agency,
      selectedCampaignIds,
    });
    if (!scopeFilters) return;
    const { start, end } = resolveDateRange({
      datePreset,
      dateRange: customDateRange,
      campaignDurations: filtered.map((item) => item.duration),
      now: new Date(),
    });
    FilterStore$.resolvedDateRange.set({ end, start });
    const scope = { endDate: end, scopeFilters, startDate: start };
    fetchArrivalPoiReport(scope);
    fetchArrivalReport(scope);
    fetchAudienceSegment(scope);
    fetchChannelBreakdownReport(scope);
    fetchCreativePerformance(scope);
    fetchDeviceBrowserReport(scope);
    fetchGeoReport(scope);
    fetchPerformanceReport(scope);
    fetchPerformanceTable(scope);
    fetchVideoFunnelReport(scope);
    fetchViewabilityReport(scope);
  }, [
    advertiser,
    advertisers,
    agencies,
    agency,
    agencyUser,
    campaigns,
    campaignsLoading,
    customDateRange,
    datePreset,
    selectedCampaignIds,
  ]);

  useUnmountEffect(clearResolvedDateRange);

  // Non-video campaigns drop Viewability and move Channel into Row 3. The
  // layout only changes once the video report settles, so loading or a failed
  // refetch never flashes the other layout. (A hook, so it sits above the
  // early return below.)
  const showVideo = useHasVideoLayout({
    hasVideo: hasVideoData(videoFunnelRows),
    isSettled:
      !isTransitioning &&
      videoFunnelLoadedKey !== null &&
      !videoFunnelLoading &&
      videoFunnelError === null,
  });

  if (
    rootEmpty ||
    advertiserEmpty ||
    (!isTransitioning && selectedCampaignIds.length === 0)
  ) {
    return <CampaignEmptyState variant={agencyUser ? "agency" : "infillion"} />;
  }

  const headerProps =
    selectedCampaigns.length > 0
      ? deriveCampaignOverviewHeaderProps(
          selectedCampaigns,
          advertisers,
          agencies,
          new Date()
        )
      : null;

  return (
    <div className={styles.overview}>
      {isTransitioning ? (
        <div className={styles.headerSkeleton}>
          <Skeleton height="1.75rem" width="14rem" />
          <Skeleton height="1rem" width="22rem" />
        </div>
      ) : (
        headerProps && (
          <CampaignOverviewHeader
            status={headerProps.status}
            subtitleParts={headerProps.subtitleParts}
            title={headerProps.title}
          />
        )
      )}
      <KpiTiles
        arrivalError={arrivalError}
        arrivalLoading={isTransitioning || arrivalLoading}
        arrivalRows={arrivalRows}
        error={reportingError}
        isLoading={isTransitioning || reportingLoading}
        rows={reportingRows}
      />
      <div className={showVideo ? styles.rowTwoThirds : styles.row}>
        <PerformanceChart
          error={reportingError}
          isLoading={isTransitioning || reportingLoading}
          rows={reportingRows}
        />
        {showVideo && (
          <div className={styles.stack}>
            <ViewabilityWidget
              error={viewabilityError}
              isLoading={isTransitioning || viewabilityLoading}
              rows={viewabilityRows}
            />
            <ChannelBreakdownWidget
              error={channelError}
              isLoading={isTransitioning || channelLoading}
              rows={channelRows}
            />
          </div>
        )}
      </div>
      <div className={styles.rowThirds}>
        <CreativePerformanceWidget
          error={creativeError}
          isLoading={isTransitioning || creativeLoading}
          rows={creativeRows}
        />
        {showVideo ? (
          <VideoFunnelWidget
            error={videoFunnelError}
            isLoading={isTransitioning || videoFunnelLoading}
            rows={videoFunnelRows}
          />
        ) : (
          <ChannelBreakdownWidget
            error={channelError}
            isLoading={isTransitioning || channelLoading}
            layout="stacked"
            rows={channelRows}
          />
        )}
      </div>
      <div className={styles.rowTopPerformers}>
        <TopPerformers
          audience={{
            error: audienceSegmentError,
            isLoading: isTransitioning || audienceSegmentLoading,
            rows: audienceSegmentRows,
          }}
          creative={{
            error: creativeError,
            isLoading: isTransitioning || creativeLoading,
            rows: creativeRows,
          }}
          deviceBrowser={{
            error: deviceBrowserError,
            isLoading: isTransitioning || deviceBrowserLoading,
            rows: deviceBrowserRows,
          }}
        />
      </div>
      <div className={styles.rowThreeFifths}>
        <GeoWidget
          error={geoError}
          isLoading={isTransitioning || geoLoading}
          rows={geoRows}
        />
      </div>
      <div className={styles.rowQuarter}>
        <ArrivalWidget
          error={arrivalError}
          isLoading={isTransitioning || arrivalLoading}
          rows={arrivalRows}
        />
        <ArrivalPoiTable
          error={arrivalPoiError}
          isLoading={isTransitioning || arrivalPoiLoading}
          rows={arrivalPoiRows}
        />
      </div>
      <PerformanceTable
        error={tableError}
        isLoading={isTransitioning || tableLoading}
        rows={tableRows}
      />
    </div>
  );
};

export default CampaignOverviewPage;
